import { useEffect, useRef, useState } from 'react';
import { Drawer } from 'antd';

export const OrientationAlert = () => {
  const previousOrientation = useRef<'portrait' | 'landscape'>('portrait');
  const hasShownPrompt = useRef(false);
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);

  // Check if device is iOS
  const isIOSDevice = () => {
    if (typeof window === 'undefined') return false;

    const userAgent = navigator.userAgent.toLowerCase();
    return (
      /iphone|ipad|ipod/i.test(userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    );
  };

  // Check if device is mobile and not iOS (Android only)
  const isAndroidMobileDevice = () => {
    if (typeof window === 'undefined') return false;

    // Check user agent
    const userAgent = navigator.userAgent.toLowerCase();
    const isMobileByUA = /android|webos|blackberry|iemobile|opera mini/i.test(
      userAgent,
    );

    // Check if device has touch capabilities
    const hasTouchScreen =
      'ontouchstart' in window || navigator.maxTouchPoints > 0;

    // Check screen dimensions (mobile typically < 1024px)
    const isSmallScreen = window.innerWidth <= 1024;

    // Combine checks: mobile user agent OR (has touch + small screen) AND not iOS
    return (
      (isMobileByUA || (hasTouchScreen && isSmallScreen)) && !isIOSDevice()
    );
  };

  const toggleFullscreen = () => {
    if (typeof window === 'undefined') return;

    try {
      if (!document.fullscreenElement) {
        // Enter fullscreen
        if (document.documentElement.requestFullscreen) {
          document.documentElement.requestFullscreen();
        } else if ((document.documentElement as any).webkitRequestFullscreen) {
          (document.documentElement as any).webkitRequestFullscreen();
        } else if ((document.documentElement as any).msRequestFullscreen) {
          (document.documentElement as any).msRequestFullscreen();
        }
      } else {
        // Exit fullscreen
        if (document.exitFullscreen) {
          document.exitFullscreen();
        } else if ((document as any).webkitExitFullscreen) {
          (document as any).webkitExitFullscreen();
        } else if ((document as any).msExitFullscreen) {
          (document as any).msExitFullscreen();
        }
      }
      // Close drawer after action
      setIsDrawerVisible(false);
    } catch (error) {
      console.log('Fullscreen toggle failed:', error);
    }
  };

  useEffect(() => {
    const updateOrientation = () => {
      if (typeof window === 'undefined') return;

      // Detect current orientation
      const isLandscape = window.innerWidth > window.innerHeight;
      const currentOrientation: 'portrait' | 'landscape' = isLandscape
        ? 'landscape'
        : 'portrait';

      // Check if orientation changed from portrait to landscape (only on Android mobile)
      if (
        previousOrientation.current === 'portrait' &&
        currentOrientation === 'landscape' &&
        !hasShownPrompt.current &&
        isAndroidMobileDevice()
      ) {
        // Show fullscreen drawer when switching to landscape on mobile
        setIsDrawerVisible(true);
        hasShownPrompt.current = true;
      }

      // Hide drawer and reset flag when switching back to portrait
      if (currentOrientation === 'portrait') {
        setIsDrawerVisible(false);
        hasShownPrompt.current = false;
      }

      // Update previous orientation
      previousOrientation.current = currentOrientation;
    };

    // Set initial orientation and ensure drawer is hidden if in portrait
    updateOrientation();

    // Ensure drawer is hidden if starting in portrait mode
    if (typeof window !== 'undefined') {
      const isLandscape = window.innerWidth > window.innerHeight;
      if (!isLandscape) {
        setIsDrawerVisible(false);
      }
    }

    // Add event listeners for orientation changes
    window.addEventListener('orientationchange', updateOrientation);
    window.addEventListener('resize', updateOrientation);

    // Clean up event listeners
    return () => {
      window.removeEventListener('orientationchange', updateOrientation);
      window.removeEventListener('resize', updateOrientation);
    };
  }, [isAndroidMobileDevice]);

  return (
    <Drawer
      placement="bottom"
      height={60}
      open={isDrawerVisible}
      onClose={() => setIsDrawerVisible(false)}
      mask={false}
      closable={false}
      styles={{
        body: {
          padding: '0',
          margin: '0',
          background: 'rgba(0,0,0,0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          position: 'relative',
        },
        wrapper: {
          background: 'transparent',
        },
        content: {
          background: 'transparent',
        },
      }}
      onClick={toggleFullscreen}
    >
      <div
        style={{
          color: 'white',
          fontSize: '12px',
          textAlign: 'center',
          userSelect: 'none',
        }}
      >
        Open Fullscreen
      </div>

      {/* Close button (X) ở góc phải */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          right: '16px',
          transform: 'translateY(-50%)',
          width: '24px',
          height: '24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          color: 'white',
          fontSize: '16px',
          fontWeight: 'bold',
          userSelect: 'none',
          zIndex: 1000,
        }}
        onClick={e => {
          e.stopPropagation(); // Prevent triggering fullscreen toggle
          setIsDrawerVisible(false);
        }}
      >
        ×
      </div>
    </Drawer>
  );
};
